import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, CalendarClock, CheckCircle2, Loader2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Today's date (YYYY-MM-DD) in IST — snapshots are keyed by IST calendar day.
const istToday = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return parts; // en-CA gives YYYY-MM-DD
};

const MarketSnapshotTab = () => {
  const today = istToday();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);

  const loadSnapshot = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/market-snapshot/${today}`, { cache: 'no-store' });
      if (r.status === 202) {
        setSnapshot(null);
        return { building: true };
      }
      if (!r.ok) {
        setSnapshot(null);
        return { building: false };
      }
      const data = await r.json();
      setSnapshot(data);
      return { building: false, data };
    } catch (_e) {
      return { building: false };
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  const rebuild = async () => {
    if (!window.confirm("Rebuild today's Market Snapshot now? This re-scrapes all live sources and overwrites the existing snapshot (takes ~60-90s).")) return;
    setBuilding(true);
    const prevGeneratedAt = snapshot?.generated_at;
    try {
      const r = await fetch(`${API_URL}/api/admin/market-snapshot/build-now`, {
        method: 'POST',
        cache: 'no-store',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Build failed to start');
      toast.success('🛠️ Rebuild started — refreshing automatically in ~90s.', { duration: 6000 });

      // Poll every 15s (max ~3 min) until generated_at changes / snapshot appears.
      let attempts = 0;
      const maxAttempts = 12;
      const poll = async () => {
        attempts += 1;
        const res = await loadSnapshot();
        const fresh = res?.data && res.data.generated_at !== prevGeneratedAt;
        if (fresh) {
          toast.success("✅ Today's snapshot rebuilt successfully.");
          setBuilding(false);
          return;
        }
        if (attempts >= maxAttempts) {
          toast.info('Still generating — check back shortly and hit Refresh.', { duration: 6000 });
          setBuilding(false);
          return;
        }
        setTimeout(poll, 15000);
      };
      setTimeout(poll, 15000);
    } catch (e) {
      toast.error(e.message || 'Failed to trigger rebuild');
      setBuilding(false);
    }
  };

  const nifty = snapshot?.indices?.['NIFTY 50'];
  const bnf = snapshot?.indices?.['BANK NIFTY'];
  const vix = snapshot?.indices?.['INDIA VIX'];

  const IndexStat = ({ label, node }) => (
    <div className="rounded-lg border border-amber-200 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-bold text-gray-900">
        {node?.ltp != null ? Number(node.ltp).toLocaleString('en-IN') : '—'}
      </div>
      {node?.change_percent != null && (
        <div className={`text-xs font-medium ${node.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {node.change_percent >= 0 ? '+' : ''}{Number(node.change_percent).toFixed(2)}%
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="market-snapshot-tab">
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <CalendarClock className="w-5 h-5 text-amber-600" />
                Daily Market Snapshot
              </CardTitle>
              <CardDescription>
                Auto-builds at <strong>4:00 PM IST</strong>. Use the button to rebuild <strong>today ({today})</strong> on demand.
              </CardDescription>
            </div>
            <Button
              onClick={rebuild}
              disabled={building}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-md"
              data-testid="rebuild-snapshot-btn"
            >
              {building ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rebuilding…</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-1" /> Rebuild Today's Snapshot</>
              )}
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="w-4 h-4 text-purple-600" />
            Today's Snapshot Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-gray-500">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Loading…
            </div>
          ) : !snapshot ? (
            <div className="py-6 text-center" data-testid="snapshot-empty-state">
              <p className="text-sm text-gray-500">
                {building ? 'Generating today\'s snapshot…' : 'No snapshot for today yet. Click "Rebuild Today\'s Snapshot" to generate it now.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="snapshot-status-block">
              <div className="flex items-center gap-2 text-xs text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                <span data-testid="snapshot-generated-at">
                  Generated at {new Date(snapshot.generated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
                </span>
                {snapshot.fno_source && (
                  <Badge variant="outline" className="text-[10px]">source: {snapshot.fno_source}</Badge>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <IndexStat label="NIFTY 50" node={nifty} />
                <IndexStat label="BANK NIFTY" node={bnf} />
                <IndexStat label="INDIA VIX" node={vix} />
              </div>

              {snapshot.verdict_md && (
                <div className="bg-gradient-to-br from-zinc-900 to-black rounded-lg p-4 text-white text-sm leading-relaxed border border-amber-500/20" data-testid="snapshot-verdict">
                  <div className="text-amber-400 font-bold mb-2">📊 AI Verdict · {snapshot.date}</div>
                  <pre className="whitespace-pre-wrap font-sans">{snapshot.verdict_md}</pre>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MarketSnapshotTab;
